import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SchedulerEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  description?: string;
}

@Component({
  selector: 'app-scheduler',
  imports: [CommonModule],
  template: `
    <div class="w-full">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <button
            type="button"
            (click)="previousPeriod()"
            class="p-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {{ currentPeriodLabel() }}
          </h2>
          <button
            type="button"
            (click)="nextPeriod()"
            class="p-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            (click)="viewMode.set('day')"
            [class]="getViewButtonClasses('day')">
            Day
          </button>
          <button
            type="button"
            (click)="viewMode.set('week')"
            [class]="getViewButtonClasses('week')">
            Week
          </button>
          <button
            type="button"
            (click)="viewMode.set('month')"
            [class]="getViewButtonClasses('month')">
            Month
          </button>
        </div>
      </div>
      
      <div class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div class="bg-gray-50 dark:bg-gray-800 p-4 text-center">
          <p class="text-gray-600 dark:text-gray-400">
            Scheduler Calendar View
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Events: {{ events().length }}
          </p>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class SchedulerComponent {
  events = input<SchedulerEvent[]>([]);
  viewMode = signal<'day' | 'week' | 'month'>('month');
  currentDate = signal(new Date());

  eventClickEvent = output<SchedulerEvent>();
  dateClickEvent = output<Date>();

  currentPeriodLabel = computed(() => {
    const date = this.currentDate();
    if (this.viewMode() === 'month') {
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (this.viewMode() === 'week') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  });

  previousPeriod(): void {
    const date = new Date(this.currentDate());
    if (this.viewMode() === 'month') {
      date.setMonth(date.getMonth() - 1);
    } else if (this.viewMode() === 'week') {
      date.setDate(date.getDate() - 7);
    } else {
      date.setDate(date.getDate() - 1);
    }
    this.currentDate.set(date);
  }

  nextPeriod(): void {
    const date = new Date(this.currentDate());
    if (this.viewMode() === 'month') {
      date.setMonth(date.getMonth() + 1);
    } else if (this.viewMode() === 'week') {
      date.setDate(date.getDate() + 7);
    } else {
      date.setDate(date.getDate() + 1);
    }
    this.currentDate.set(date);
  }

  getViewButtonClasses(mode: string): string {
    const classes = ['px-4', 'py-2', 'rounded-md', 'font-medium', 'transition-colors'];
    if (this.viewMode() === mode) {
      classes.push('bg-blue-600', 'text-white', 'dark:bg-blue-500');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-700', 'dark:text-gray-300', 'border', 'border-gray-300', 'dark:border-gray-600', 'hover:bg-gray-50', 'dark:hover:bg-gray-700');
    }
    return classes.join(' ');
  }
}

